import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DocumentWorkspace,
  FormAlert,
  FormDialog,
  FormCard,
  FormSection,
  FormField,
  FormGrid,
  SettingsButton,
  type DocJson,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/document/DocumentEditor.css";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import {
  orgGuidanceApi,
  type OrgGuidanceBundle,
  type OrgGuidanceVersionListItem,
} from "../../api/orgGuidanceApi";
import { platformApi, type PlatformTenant } from "../../api/platformApi";
import { formatOrganizationLabel } from "../organizations/orgDisplay";
import { toClientApiError } from "../../api/httpClient";
import { getRealtimeSocket } from "../../realtime/socketService";
import { useAuth } from "../../auth/AuthProvider";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { QuietButton } from "../../ui/QuietButton";

type CommentDraft = {
  text: string;
  markId: string;
  kind: "comment" | "edit_request";
  body: string;
};

export function OrgGuidancePage() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPlatformEditor =
    auth.user?.platformRoles?.includes("super_admin") === true ||
    auth.user?.platformRoles?.includes("technical_staff") === true;

  const [platformTenants, setPlatformTenants] = useState<PlatformTenant[]>([]);
  const [targetTenantId, setTargetTenantId] = useState<string | null>(null);
  const [bundle, setBundle] = useState<OrgGuidanceBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<string | null>(null);
  const [draftDoc, setDraftDoc] = useState<DocJson | null>(null);
  const [commentDraft, setCommentDraft] = useState<CommentDraft | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState<OrgGuidanceVersionListItem[]>([]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentVersionRef = useRef(1);

  const apiTenantId = isPlatformEditor ? targetTenantId ?? undefined : undefined;

  const selectedOrg = useMemo(() => {
    if (!isPlatformEditor || !targetTenantId) return null;
    return platformTenants.find((t) => t.id === targetTenantId) ?? null;
  }, [isPlatformEditor, targetTenantId, platformTenants]);
  const selectedOrgName = selectedOrg
    ? formatOrganizationLabel(selectedOrg.code, selectedOrg.name)
    : null;

  useEffect(() => {
    if (!isPlatformEditor) {
      setTargetTenantId(null);
      return;
    }
    let cancelled = false;
    void platformApi
      .listTenants()
      .then((items) => {
        if (cancelled) return;
        setPlatformTenants(items);
        const fromUrl = searchParams.get("tenantId");
        const fromCodeRaw = searchParams.get("code");
        const fromCode = fromCodeRaw ? Number(fromCodeRaw) : NaN;
        const byCode =
          Number.isInteger(fromCode) && fromCode > 0
            ? items.find((t) => t.code === fromCode)
            : undefined;
        const activeId = auth.activeTenant?.tenantId ?? null;
        const next =
          (fromUrl && items.some((t) => t.id === fromUrl) ? fromUrl : null) ||
          byCode?.id ||
          (activeId && items.some((t) => t.id === activeId) ? activeId : null) ||
          items[0]?.id ||
          null;
        setTargetTenantId(next);
        if (next && fromUrl !== next) {
          setSearchParams({ tenantId: next }, { replace: true });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(toClientApiError(err).message || "שגיאה בטעינת רשימת ארגונים");
        }
      });
    return () => {
      cancelled = true;
    };
    // Intentionally exclude searchParams from deps — URL is seed only on mount / role change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatformEditor, auth.activeTenant?.tenantId]);

  useEffect(() => {
    if (!isPlatformEditor || platformTenants.length === 0) return;
    const fromUrl = searchParams.get("tenantId");
    const fromCodeRaw = searchParams.get("code");
    const fromCode = fromCodeRaw ? Number(fromCodeRaw) : NaN;
    const byCode =
      Number.isInteger(fromCode) && fromCode > 0
        ? platformTenants.find((t) => t.code === fromCode)
        : undefined;
    const resolvedId =
      fromUrl && platformTenants.some((t) => t.id === fromUrl)
        ? fromUrl
        : byCode?.id;
    if (
      resolvedId &&
      resolvedId !== targetTenantId
    ) {
      setTargetTenantId(resolvedId);
      setBundle(null);
      setDraftDoc(null);
    }
  }, [searchParams, isPlatformEditor, platformTenants, targetTenantId]);

  const load = useCallback(async () => {
    if (isPlatformEditor && !targetTenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await orgGuidanceApi.get(apiTenantId);
      setBundle(data);
      setDraftDoc(data.item.bodyJson as DocJson);
      contentVersionRef.current = data.item.contentVersion;
      setSaveState(
        data.item.updatedByName
          ? `עודכן לאחרונה ע״י ${data.item.updatedByName}`
          : null,
      );
    } catch (err) {
      setBundle(null);
      setDraftDoc(null);
      setError(toClientApiError(err).message || "שגיאה בטעינת מסמך הנחיות");
    } finally {
      setLoading(false);
    }
  }, [apiTenantId, isPlatformEditor, targetTenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const socket = getRealtimeSocket();
    const refresh = () => {
      void load();
    };
    socket.on("org_guidance.document.updated", refresh);
    socket.on("org_guidance.side_note.changed", refresh);
    socket.on("org_guidance.comment.changed", refresh);
    return () => {
      socket.off("org_guidance.document.updated", refresh);
      socket.off("org_guidance.side_note.changed", refresh);
      socket.off("org_guidance.comment.changed", refresh);
    };
  }, [load]);

  const persist = useCallback(
    async (next: DocJson) => {
      if (!bundle?.item.capabilities.canEdit) return;
      setSaveState("שומר...");
      try {
        const data = await orgGuidanceApi.update(
          {
            contentVersion: contentVersionRef.current,
            bodyJson: next,
            title: "מסמך הנחיות לארגון זה",
          },
          apiTenantId,
        );
        setBundle(data);
        setDraftDoc(data.item.bodyJson as DocJson);
        contentVersionRef.current = data.item.contentVersion;
        setSaveState("נשמר");
      } catch (err) {
        const apiErr = toClientApiError(err);
        if (apiErr.status === 409) {
          setSaveState("התנגשות גרסה — מרענן...");
          await load();
          setError("המסמך עודכן בינתיים. התוכן רוענן מהשרת.");
        } else {
          setError(apiErr.message || "שגיאה בשמירה");
          setSaveState("שגיאת שמירה");
        }
      }
    },
    [apiTenantId, bundle?.item.capabilities.canEdit, load],
  );

  const onChangeDoc = (next: DocJson) => {
    setDraftDoc(next);
    if (!bundle?.item.capabilities.canEdit) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("ממתין לשמירה...");
    saveTimer.current = setTimeout(() => {
      void persist(next);
    }, 900);
  };

  const onSelectOrg = (tenantId: string) => {
    setTargetTenantId(tenantId);
    setSearchParams({ tenantId }, { replace: true });
    setBundle(null);
    setDraftDoc(null);
  };

  const caps = bundle?.item.capabilities;
  const docTitle = selectedOrgName
    ? `מסמך הנחיות — ${selectedOrgName}`
    : "מסמך הנחיות לארגון זה";

  if (loading && !bundle) {
    return (
      <section className="page-section page-section--list org-guidance-page" aria-label="מסמך הנחיות">
        טוען מסמך הנחיות...
      </section>
    );
  }

  if (error && !bundle) {
    return (
      <section className="page-section page-section--list org-guidance-page" aria-label="מסמך הנחיות">
        {isPlatformEditor && platformTenants.length > 0 ? (
          <div className="page-section__filters">
            <FormField
              label="ארגון"
              type="select"
              value={targetTenantId || ""}
              onChange={onSelectOrg}
              options={platformTenants.map((tenant) => ({
                value: tenant.id,
                label: formatOrganizationLabel(tenant.code, tenant.name),
              }))}
            />
          </div>
        ) : null}
        <FormAlert severity="error">{error}</FormAlert>
        <div className="org-guidance-page__actions">
          <SettingsButton
            type="button"
            variant="primarySettings"
            size="sm"
            onClick={() => void load()}
          >
            נסה שוב
          </SettingsButton>
        </div>
      </section>
    );
  }

  return (
    <section className="page-section page-section--list org-guidance-page" aria-label="מסמך הנחיות">
      <ListPageHeader
        screen
        onRefresh={() => void load()}
        secondary={
          caps?.canEdit ? (
            <SettingsButton
              type="button"
              variant="ghostSettings"
              size="sm"
              onClick={() => {
                void orgGuidanceApi.listVersions(apiTenantId).then((res) => {
                  setVersions(res.items);
                  setVersionsOpen(true);
                });
              }}
            >
              היסטוריית גרסאות
            </SettingsButton>
          ) : null
        }
      />

      {isPlatformEditor ? (
        <div className="page-section__filters">
          <FormField
            label="ארגון"
            type="select"
            value={targetTenantId || ""}
            onChange={onSelectOrg}
            options={platformTenants.map((tenant) => ({
              value: tenant.id,
              label: formatOrganizationLabel(tenant.code, tenant.name),
            }))}
          />
        </div>
      ) : null}

      {selectedOrgName ? (
        <FormAlert severity="info">מסמך הנחיות עבור: {selectedOrgName}</FormAlert>
      ) : null}

      {error ? <FormAlert severity="error">{error}</FormAlert> : null}

      <DocumentWorkspace
        title={docTitle}
        value={draftDoc}
        onChange={onChangeDoc}
        readOnly={!caps?.canEdit}
        saveState={saveState}
        outlineItems={bundle?.item.outlineJson}
        sideNotes={bundle?.sideNotes || []}
        comments={bundle?.comments || []}
        canComment={Boolean(caps?.canComment)}
        canModerate={Boolean(caps?.canModerate)}
        onRequestComment={(selection) => {
          setCommentDraft({
            text: selection.text,
            markId: selection.markId,
            kind: "edit_request",
            body: "",
          });
        }}
        onAddSideNote={(body) => {
          void orgGuidanceApi.createSideNote({ body }, apiTenantId).then(() => load());
        }}
        onDeleteSideNote={(noteId) => {
          void orgGuidanceApi.deleteSideNote(noteId, apiTenantId).then(() => load());
        }}
        onReply={(commentId, body) => {
          void orgGuidanceApi.reply(commentId, body, apiTenantId).then(() => load());
        }}
        onModerate={(commentId, status) => {
          void orgGuidanceApi
            .moderate(
              commentId,
              {
                status: status as
                  | "approved"
                  | "in_progress"
                  | "handled"
                  | "rejected"
                  | "open",
              },
              apiTenantId,
            )
            .then(() => load());
        }}
      />

      <FormDialog
        open={Boolean(commentDraft)}
        onClose={() => setCommentDraft(null)}
        size="sm"
      >
        <FormCard
          title="שליחת הערה / בקשת עריכה"
          onCancel={() => setCommentDraft(null)}
          onSave={() => {
            if (!commentDraft?.body.trim()) return;
            void orgGuidanceApi
              .createComment(
                {
                  body: commentDraft.body.trim(),
                  kind: commentDraft.kind,
                  quoteText: commentDraft.text,
                  markId: commentDraft.markId,
                },
                apiTenantId,
              )
              .then(() => {
                setCommentDraft(null);
                return load();
              })
              .catch((err) => setError(toClientApiError(err).message));
          }}
          saveLabel="שלח"
          cancelLabel="ביטול"
        >
          <FormSection>
            <FormGrid cols={1}>
              <FormField label="הקטע המסומן" value={commentDraft?.text || ""} disabled />
              <FormField
                label="סוג"
                type="select"
                value={commentDraft?.kind || "comment"}
                options={[
                  { value: "comment", label: "הערה" },
                  { value: "edit_request", label: "בקשת עריכה" },
                ]}
                onChange={(value) =>
                  setCommentDraft((prev) =>
                    prev
                      ? {
                          ...prev,
                          kind: value === "edit_request" ? "edit_request" : "comment",
                        }
                      : prev,
                  )
                }
              />
              <FormField
                label="תוכן ההערה"
                type="textarea"
                rows={4}
                value={commentDraft?.body || ""}
                onChange={(value) =>
                  setCommentDraft((prev) => (prev ? { ...prev, body: value } : prev))
                }
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>

      <FormDialog open={versionsOpen} onClose={() => setVersionsOpen(false)} size="md">
        <FormCard
          title="היסטוריית גרסאות"
          onCancel={() => setVersionsOpen(false)}
          cancelLabel="סגור"
        >
          <FormSection>
            {versions.length === 0 ? (
              <p>אין גרסאות שמורות</p>
            ) : (
              <ul className="org-guidance-versions">
                {versions.map((v) => (
                  <li key={v.id}>
                    <div>
                      <strong>גרסה {v.contentVersion}</strong>
                      <span>
                        {v.createdByName || "מערכת"} ·{" "}
                        {new Date(v.createdAt).toLocaleString("he-IL")}
                      </span>
                      {v.note ? <div>{v.note}</div> : null}
                    </div>
                    <QuietButton
                      tone="accent"
                      onClick={() => {
                        void orgGuidanceApi.restoreVersion(v.id, apiTenantId).then((data) => {
                          setBundle(data);
                          setDraftDoc(data.item.bodyJson as DocJson);
                          contentVersionRef.current = data.item.contentVersion;
                          setVersionsOpen(false);
                          setSaveState("שוחזרה גרסה");
                        });
                      }}
                    >
                      שחזר
                    </QuietButton>
                  </li>
                ))}
              </ul>
            )}
          </FormSection>
        </FormCard>
      </FormDialog>
    </section>
  );
}

export default OrgGuidancePage;
